import { Trans, useTranslation } from 'react-i18next';
import { EnvService } from '@hub/services/core/env-service';
import { trackMixpanel } from '@monorepo/client-common';
import { Button, Flex, TextComponent } from '@monorepo/react-components';
import { useLoading, useResponsiveness } from '@monorepo/react-hooks';
import { EventTypes, MailOtpSource, ReceivedMailState } from '@monorepo/types';
import { useStore } from '../../helpers/use-store';
import { ContactSupportButton } from './contact-support-button';

export const NotReceivedMail = ({
  header,
  message,
  source,
  sourceId,
  displayName,
  setReceivedMailState,
}: {
  header: string;
  message: string;
  source: MailOtpSource;
  sourceId: string;
  setReceivedMailState: React.Dispatch<
    React.SetStateAction<ReceivedMailState | undefined>
  >;
  displayName?: string;
}) => {
  const { isLoading, setLoadingState } = useLoading();
  const { isMobile } = useResponsiveness();

  const {
    dataStore: { mailOtpStore, userStore },
  } = useStore();
  const { t } = useTranslation(['mailConfirmation']);

  const handleSendMailClick = async () => {
    try {
      setLoadingState('Loading');
      const { sent, reason } = await mailOtpStore.sendMail(
        source,
        sourceId,
        displayName,
      );

      trackMixpanel({
        event: EventTypes.MAIL_OTP_SENT,
        properties: {
          email: userStore.currentUser.email,
          platform: EnvService.get().application,
          source,
          sourceId,
          displayName,
        },
      });

      if (!sent) {
        setReceivedMailState(reason ?? ReceivedMailState.ERROR);
        setLoadingState('Error');
        return;
      }

      setReceivedMailState(ReceivedMailState.RECEIVED);
      setLoadingState('Loaded');
    } catch (error: unknown) {
      console.error('Error sending mail:', error);
      setReceivedMailState(ReceivedMailState.ERROR);
      setLoadingState('Error');
    }
  };

  return (
    <Flex align="center" vertical gap={15} className="mail-modal">
      <TextComponent
        level={2}
        weight={600}
        className="title"
        textAlign="center"
      >
        {header}
      </TextComponent>
      <TextComponent textAlign="center">{message}</TextComponent>
      <TextComponent level={3} weight={500}>
        {userStore.currentUser.email}
      </TextComponent>
      <TextComponent level={8} textAlign="center">
        <Trans
          ns="mailConfirmation"
          i18nKey="contact_support_to_keep_your_account_safe"
          components={[isMobile ? <br /> : <></>]}
        />
      </TextComponent>
      <Flex
        gap={isMobile ? 0 : 20}
        style={{
          flexDirection: isMobile ? 'column' : 'row-reverse',
        }}
      >
        <Button
          loading={isLoading}
          onClick={handleSendMailClick}
          type="primary"
        >
          {t('mailConfirmation:send_me_code')}
        </Button>
        <ContactSupportButton flow="not-received-mail" isLoading={isLoading} />
      </Flex>
    </Flex>
  );
};
